import { useState } from 'react';

/** Form validation shared by every form.
 *
 *  A schema maps a field name to one rule or a list of rules: { email: [rules.required('Email'), rules.email()] }.
 *  A rule is (value, allValues) => message, and returns '' when the value is fine. The first failing rule wins.
 *  Rules skip blank values (except `required`), so optional fields are only checked when something is typed.
 *  The server repeats these checks and has the final say. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const blank = (v) => v === undefined || v === null || String(v).trim() === '';
const text = (v) => String(v ?? '').trim();
const when = (check) => (v, all) => (blank(v) ? '' : check(text(v), all));

const parseDay = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getFullYear() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[3]) ? d : null;
};

export const rules = {
  required: (label) => (v) => (blank(v) ? `${label} is required.` : ''),
  email: (label = 'Email') => when((v) => (EMAIL.test(v) ? '' : `${label} must be a valid email address, like name@company.com.`)),
  phone: (label = 'Phone') => when((v) => {
    const digits = v.replace(/\D/g, '');
    return /^[+0-9()\-.\sx]+$/.test(v) && digits.length >= 7 && digits.length <= 20 ? '' : `${label} must be a phone number with 7 to 20 digits.`;
  }),
  url: (label = 'Website') => when((v) => {
    const host = v.replace(/^https?:\/\//i, '').split('/')[0];
    return /^(https?:\/\/)?[^\s/$.?#][^\s]*$/i.test(v) && host.includes('.') && !/\s/.test(v) ? '' : `${label} must be a web address, like www.example.com.`;
  }),
  maxLength: (n, label) => (v) => (text(v).length > n ? `${label} must be at most ${n} characters.` : ''),
  minLength: (n, label) => when((v) => (v.length < n ? `${label} must be at least ${n} characters.` : '')),
  number: (label, { min, max, above } = {}) => when((v) => {
    const n = Number(v.replace(/[$,\s]/g, ''));
    if (!Number.isFinite(n)) return `${label} must be a number.`;
    if (above !== undefined && n <= above) return `${label} must be greater than ${above}.`;
    if ((min !== undefined && n < min) || (max !== undefined && n > max)) return `${label} must be between ${min} and ${max}.`;
    return '';
  }),
  date: (label, { min = '2000-01-01', max } = {}) => when((v) => {
    const d = parseDay(v);
    if (!d) return `${label} must be a valid date.`;
    if (min && d < parseDay(min)) return `${label} cannot be before ${min.slice(0, 4)}.`;
    if (max && d > parseDay(max)) return max === dayFromNow(0) ? `${label} cannot be in the future.` : `${label} is too far in the future.`;
    return '';
  }),
  notBefore: (otherKey, label, otherLabel) => when((v, all) => {
    const a = parseDay(v), b = parseDay(text(all[otherKey]));
    return a && b && a < b ? `${label} cannot be before ${otherLabel.toLowerCase()}.` : '';
  }),
  oneOf: (options, label) => when((v) => (options.includes(v) ? '' : `Choose a valid ${label.toLowerCase()}.`)),
  matches: (otherKey, message) => (v, all) => (v !== all[otherKey] ? message : ''),
  differs: (otherKey, message) => (v, all) => (!blank(v) && v === all[otherKey] ? message : ''),
  check: (test, message) => (v, all) => (test(v, all) ? '' : message),
};

/** `original` is the saved record when editing: a field left exactly as saved is not re-checked, so an old record
 *  with a legacy value can still be opened and changed elsewhere. This mirrors the server. */
export function validate(values, schema, original) {
  const errors = {};
  Object.entries(schema).forEach(([key, list]) => {
    if (original && String(values[key] ?? '') === String(original[key] ?? '')) return;
    for (const rule of [].concat(list)) {
      const message = rule(values[key], values);
      if (message) { errors[key] = message; break; }
    }
  });
  return errors;
}

/** Today as YYYY-MM-DD in the user's time zone, plus `days`. */
export const dayFromNow = (days = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Binds a schema to the current form values.
 *  shown(key)  the message to display: set once the field was left or a submit was tried, or when the server named it.
 *  props(key)  spread on the control: marks it invalid for assistive technology and records the first blur.
 *  touch(key)  reveal a field's message now, for groups of checkboxes that have no single control to blur.
 *  check()     call on submit: reveals every message, focuses the first bad field, returns true when all is fine.
 *  fail(err)   feed a server error: its per-field messages show under the right inputs until that input changes.
 *  summary     one line for the top of the form once a submit was tried while something is still wrong. */
export function useFormValidation(values, schema, original) {
  const [touched, setTouched] = useState({});
  const [server, setServer] = useState({});
  const [tried, setTried] = useState(false);
  const errors = validate(values, schema, original);
  const count = Object.keys(errors).length;
  const summary = tried && count ? `Please fix the ${count} highlighted field${count > 1 ? 's' : ''} before saving.` : '';
  const fromServer = (key) => (server[key] && server[key].value === values[key] ? server[key].message : '');
  const shown = (key) => (touched[key] && errors[key]) || fromServer(key) || '';
  const props = (key) => ({
    invalid: Boolean(shown(key)),
    'aria-invalid': shown(key) ? true : undefined,
    onBlur: () => setTouched((t) => (t[key] ? t : { ...t, [key]: true })),
  });
  const touch = (key) => setTouched((t) => (t[key] ? t : { ...t, [key]: true }));
  const focusFirst = () => setTimeout(() => {
    const el = document.querySelector('[aria-invalid="true"]');
    if (el) el.focus();
  }, 0);
  const check = () => {
    setTried(true);
    setTouched(Object.fromEntries(Object.keys(schema).map((k) => [k, true])));
    if (count) { focusFirst(); return false; }
    return true;
  };
  const fail = (error) => {
    const fields = error && error.fields;
    if (!fields) return false;
    setServer(Object.fromEntries(Object.entries(fields).map(([k, message]) => [k, { message, value: values[k] }])));
    focusFirst();
    return true;
  };
  return { errors, shown, props, touch, check, fail, summary, valid: count === 0 };
}
